import {Box, CircularProgress, Paper, Typography} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import {Delete} from "@mui/icons-material";
import {OutlinedDropdownForm} from "../../../../../../components/base/dropdown/outlined_dropdown";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import React, {useCallback, useState} from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";


const SearchLimitsSection = ({customer, setCustomer, values, initialValues, form, formData, required, deleting, submitting}) => {
    const [removingSearchLimits, setRemovingSearchLimits] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Calls the api for updating or creating the customer search limit type and count. In order to successfully update,
     * must have a value for type and a value for count.
     *
     * will update if the list of the searchLimitations already has a member, otherwise will create.
     *
     * @type {(function(*=, *=, *=): void)}
     */
    const changeSearchLimit = useCallback(async (searchLimitType, searchLimitCount, setLoading) => {
        setLoading(true);
        const update = !!customer.searchLimitations;
        if (update) {
            const forApi = {
                id: customer.searchLimitations?.id,
                customerId: customer?.id,
                periodType: searchLimitType,
                limit: searchLimitCount,
            }
            const response = await Api.updateSearchLimitForCustomer(forApi);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setCustomer(prevState => ({
                    ...prevState,
                    searchLimitations: {
                        id: forApi.id,
                        periodTypeId: searchLimitType.id,
                        periodTypeTitle: searchLimitType.name,
                        limit: searchLimitCount,
                    }
                }))
            }
            setLoading(false);
        } else {
            const forApi = {
                customerId: customer?.id,
                periodType: searchLimitType,
                limit: searchLimitCount,
            }
            const response = await Api.createSearchLimitForCustomer(forApi);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setCustomer(prevState => ({
                    ...prevState,
                    searchLimitations: {
                        id: response.data.id,
                        periodTypeId: searchLimitType.id,
                        periodTypeTitle: searchLimitType.name,
                        limit: searchLimitCount,
                    }
                }))
            }
            setLoading(false);
        }
    }, [customer, isMounted, setCustomer])

    /**
     * Removes the search limits of the customer by calling the api.
     */
    const removeSearchLimit = useCallback(async () => {
        setRemovingSearchLimits(true);
        const response = await Api.removeSearchLimitForCustomer(customer?.searchLimitations?.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCustomer(prevState => ({...prevState, searchLimitations: null}))
        }
        setRemovingSearchLimits(false);
    }, [customer?.searchLimitations?.id, isMounted, setCustomer])

    /**
     * Rollbacks the values of the searchLimit for the customer to their initial values.
     * @param form {any}
     * @param initialValues {any}
     */
    const rollbackSearchLimitForm = useCallback((form, initialValues) => {
        form.change('searchLimitType', initialValues.searchLimitType)
        form.change('searchLimitCount', initialValues.searchLimitCount)
    }, [])


    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', gap: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Search (lookup) Limitations
                    </Typography>
                    {
                        !!customer?.searchLimitations &&
                        <Button
                            disabled={removingSearchLimits}
                            onClick={removeSearchLimit}
                            variant={'text'}
                            color={'error'}
                            className={'mb-3'}
                        >
                            {
                                removingSearchLimits
                                    ? <CircularProgress size={20} color={'error'}/>
                                    : <Delete className={'icon normal'}/>
                            }
                            <p className='text-xs font-weight-bold ml-1'>
                                {
                                    removingSearchLimits ? 'Removing...' : "Remove"
                                }
                            </p>
                        </Button>
                    }
                </Box>
                <Grid2 container spacing={2}>
                    <Grid2 xs={12} lg={5}>
                        <OutlinedDropdownForm
                            label={'Search Limit Type'}
                            name={`searchLimitType`}
                            required={required.searchLimitType}
                            data={formData.searchLimitTypes?.map(e => ({
                                value: e,
                                label: e.name,
                                id: e.id
                            })) ?? []}
                        />
                    </Grid2>
                    <Grid2 xs={12} lg={7}>
                        <OutlinedInputForm
                            label={'Search Limit Count'}
                            name={'searchLimitCount'}
                            type={'number'}
                            required={required.searchLimitCount}
                            value={values?.searchLimitCount ?? ''}
                            onChange={(e) => (!e.target.value && form.change(e.target.name, '')) || (e.target.value && !isNaN(parseInt(e.target.value)) && form.change(e.target.name, parseInt(e.target.value)))}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <SaveOrRollback
                            column={false}
                            show={!!(
                                !deepEqual(
                                    {
                                        searchLimitType: values.searchLimitType,
                                        searchLimitCount: values.searchLimitCount
                                    },
                                    {
                                        searchLimitType: initialValues.searchLimitType,
                                        searchLimitCount: initialValues.searchLimitCount
                                    }
                                )
                                && (values.searchLimitCount !== null && !(typeof values.searchLimitCount === 'string' && !values.searchLimitCount?.length))
                                && values.searchLimitType
                                && !deleting
                            )}
                            onRollback={() => rollbackSearchLimitForm(form, initialValues)}
                            onSave={(setLoading) => !submitting && changeSearchLimit(values.searchLimitType, Number(values.searchLimitCount), setLoading)}
                        />
                    </Grid2>
                </Grid2>
            </Paper>
        </>
    );
}

export default SearchLimitsSection;